import { useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  ExpandedState,
} from '@tanstack/react-table';
import { Icon } from 'ui';
import Tooltip from 'components/ui/tooltip';
import colors from 'theme/colors';
import * as Layout from './Layout';

interface ITable<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  size: number;
  isSortable?: boolean;
  noItemsText?: string;
  nestingDepthTooltipText?: string;
  expanded?: ExpandedState | undefined;
  setExpanded?: (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => void;
}

const defaultNestingTooltipText =
  'You have reached the max nesting depth. Additional elements are not visible';

const defaultnoItemsText = 'No items to display.';

const Table = <TData extends { children?: TData[] }>({
  columns,
  data,
  size,
  isSortable = true,
  noItemsText = defaultnoItemsText,
  nestingDepthTooltipText = defaultNestingTooltipText,
  expanded,
  setExpanded,
}: ITable<TData>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns,
    manualPagination: true,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.children,
    enableSorting: isSortable,
    enableSortingRemoval: false,
    enableColumnResizing: true,
  });

  if (!data || data.length === 0) {
    return <div className="w-full py-6 text-center text-gray-400">{noItemsText}</div>;
  }

  return (
    <Layout.TableOverflow>
      <Layout.Table style={{ minWidth: `${size}px` }}>
        <Layout.TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="sticky top-0 z-10 bg-dark1">
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDirection = header.column.getIsSorted();
                return (
                  <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                    <div className="flex items-center gap-2">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span>
                        {canSort && sortDirection && (sortDirection === 'asc' ? '▼' : '▲')}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </Layout.TableHeader>

        <Layout.TableBody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell, index) => {
                const indent = index === 0 && row.depth && row.depth * 16;
                const showExpandButton = index === 0 && row.getCanExpand() && row.depth < 2;
                const showTooltipInstead = index === 0 && row.getCanExpand() && row.depth === 2;

                return (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                    }}
                  >
                    <Layout.TableCell style={{ marginLeft: `${indent}px` }} depth={row.depth}>
                      {showExpandButton && row.getCanExpand() && (
                        <button onClick={row.getToggleExpandedHandler()}>
                          {row.getIsExpanded() ? (
                            <Icon
                              size="small"
                              icon="ChevronDownIcon"
                              stroke="white"
                              rotate="180deg"
                              style={{ transitionProperty: 'none' }}
                            />
                          ) : (
                            <Icon
                              size="small"
                              icon="ChevronDownIcon"
                              stroke="white"
                              style={{ transitionProperty: 'none' }}
                            />
                          )}
                        </button>
                      )}
                      {showTooltipInstead && (
                        <Tooltip
                          tooltipContentClassName="max-w-xs text-center"
                          content={nestingDepthTooltipText}
                        >
                          <Icon size="normal" icon="InfoIcon" stroke={colors.white} />
                        </Tooltip>
                      )}
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Layout.TableCell>
                  </td>
                );
              })}
            </tr>
          ))}
        </Layout.TableBody>
      </Layout.Table>
    </Layout.TableOverflow>
  );
};

export default Table;
